import React, { useEffect, useRef } from "react";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { useLocation, useNavigationType } from "react-router-dom";

/*
 * Global cache for DataGrid pagination states.
 *
 * The cache survives component unmount/remount while the React
 * application is running.
 */
const paginationStateCache = new Map();

const getDefaultPaginationModel = initialState => {
  const model =
    initialState?.pagination?.paginationModel || {};

  return {
    page:
      typeof model.page === "number"
        ? model.page
        : 0,

    pageSize:
      typeof model.pageSize === "number"
        ? model.pageSize
        : 10,
  };
};

const PersistentDataGrid = ({
  persistenceKey,
  rows = [],
  initialState,
  onPaginationModelChange,
  ...props
}) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const apiRef = useGridApiRef();

  /*
   * IMPORTANT:
   *
   * We intentionally do NOT use location.key here.
   *
   * We want the same pagination state to be used whenever
   * this particular list page is opened again.
   *
   * Therefore:
   *
   * /members + members-list
   *
   * always maps to the same pagination state.
   */
  const cacheKey =
    `${location.pathname}:${persistenceKey || "default"}`;

  const restoredRef = useRef(false);
  const restoringRef = useRef(false);

  /*
   * Restore the saved pagination AFTER rows have loaded.
   *
   * This is important because ClientMembersList initially
   * renders with an empty rows array and receives the data
   * asynchronously from GraphQL.
   */
  useEffect(() => {
    /*
     * Wait until the DataGrid actually has data.
     */
    if (!rows || rows.length === 0) {
      return;
    }

    /*
     * Don't restore more than once for this component instance.
     */
    if (restoredRef.current) {
      return;
    }

    /*
     * Only restore the saved pagination when the page was reached via
     * normal in-app navigation (e.g. clicking the "Members List"
     * breadcrumb/link, which is a PUSH/REPLACE history action).
     *
     * When the page was reached via the browser's Back/Forward buttons
     * (a POP history action), we intentionally do NOT restore the saved
     * pagination — the grid should fall back to its default initialState
     * (first page). We also drop the stale cache entry so a later PUSH
     * navigation to this page doesn't pick up an out-of-date value.
     */
    if (navigationType === "POP") {
      paginationStateCache.delete(cacheKey);
      restoredRef.current = true;
      return;
    }

    const savedPagination =
      paginationStateCache.get(cacheKey);

    /*
     * No previous pagination state exists.
     * DataGrid will use its normal initialState.
     */
    if (!savedPagination) {
      restoredRef.current = true;
      return;
    }

    const pageSize =
      savedPagination.pageSize || 10;

    /*
     * Make sure the saved page still exists.
     *
     * Example:
     *
     * Previously:
     * 46 rows → page 4 = rows 41–46
     *
     * Later:
     * 25 rows → page 4 doesn't exist anymore.
     *
     * In that case we restore the last available page.
     */
    const lastPage = Math.max(
      0,
      Math.ceil(rows.length / pageSize) - 1
    );

    const page = Math.min(
      Math.max(savedPagination.page, 0),
      lastPage
    );

    restoringRef.current = true;

    /*
     * Restore page size first.
     */
    if (
      apiRef.current &&
      typeof apiRef.current.setPageSize === "function"
    ) {
      apiRef.current.setPageSize(pageSize);
    }

    /*
     * Then restore the page.
     */
    if (
      apiRef.current &&
      typeof apiRef.current.setPage === "function"
    ) {
      apiRef.current.setPage(page);
    }

    /*
     * Save the corrected value in case the old page
     * was no longer available.
     */
    paginationStateCache.set(cacheKey, {
      page,
      pageSize,
    });

    /*
     * setPage() can cause another pagination event.
     * Give DataGrid time to finish that operation before
     * allowing normal pagination changes to be saved.
     */
    setTimeout(() => {
      restoringRef.current = false;
    }, 0);

    restoredRef.current = true;
  }, [rows, cacheKey, apiRef, navigationType]);

  /*
   * Save pagination whenever the user changes the page
   * or page size.
   */
  const handlePaginationModelChange = model => {
    if (!restoringRef.current) {
      paginationStateCache.set(cacheKey, {
        page: model.page,
        pageSize: model.pageSize,
      });
    }

    /*
     * Preserve any pagination callback supplied by the
     * parent component.
     */
    if (onPaginationModelChange) {
      onPaginationModelChange(model);
    }
  };

  return (
    <DataGrid
      {...props}
      apiRef={apiRef}
      rows={rows}
      initialState={initialState}
      onPaginationModelChange={
        handlePaginationModelChange
      }
    />
  );
};

export default PersistentDataGrid;
